import { inject, Injectable } from '@angular/core';
import type { Camera } from '@hunting-nature/shared-core/interfaces/camera.interface';
import { AlertService } from '@hunting-nature/shared-ui/services/alert.service';
import type { RecognitionResults } from '@jcesarmobile/capacitor-ocr/dist/esm/definitions';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';

import { convertToCamera } from '../../../shared/helper/camera.helper';
import { RestService } from '../../../shared/services/rest-service';
import { HostDeviceCameraService } from '../../cameras/services/host-device-camera.service';
import { OcrService } from '../../cameras/services/ocr.service';

@Injectable({
  providedIn: 'root',
})
export class AddCameraService {
  // services
  private readonly restService = inject(RestService);
  private readonly ocrService = inject(OcrService);
  private readonly alertService = inject(AlertService);

  activateCamera$(activationCode: string): Observable<Camera> {
    return this.restService.addCamera$(activationCode).pipe(map(cameraRest => convertToCamera(undefined, cameraRest)));
  }

  async scanActivationCode(): Promise<string | undefined> {
    let ocrResult: RecognitionResults | undefined;

    try {
      ocrResult = await this.ocrService.capturePictureAndOcr();
      if (!ocrResult) {
        return;
      }
    } catch (error: unknown) {
      // TODO: handle errors properly, e.g. show alert if camera permission is denied
      //throw new ScanActivationCodeError('Error during scanning activation code', error);
      if (HostDeviceCameraService.isPermissionDenied(error)) {
        await this.alertService.error('cameras.addCamera.activation.enableMobileCameraAccess');
        return;
      }

      await this.alertService.error(error instanceof Error ? error.message : 'Unknown error while scanning activation code');

      return;
    }

    const ocrText: string = this.ocrService.convertOcrResultToString(ocrResult);
    if (!ocrText) {
      return;
    }

    const activationCode: string | undefined = this.extractActivationCode(ocrText);
    if (!activationCode) {
      return;
    }

    return activationCode;
  }

  private extractActivationCode(text: string): string | undefined {
    if (!text) {
      return undefined;
    }

    const match: RegExpMatchArray | null = new RegExp(/\b[A-Z0-9]{3}-[A-Z0-9]{3}-[A-Z0-9]{3}\b/).exec(text);

    return match ? match[0] : undefined;
  }
}
